import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getSettings, updateSettings as updateSettingsApi } from '../lib/api';

const AppContext = createContext(null);

const STORAGE_KEYS = {
  currentProject: 'wavekit.currentProject',
  currentAnalysis: 'wavekit.currentAnalysis',
};

const readStoredJson = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredJson = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    if (value == null) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
};

export function AppProvider({ children }) {
  const [currentProject, setCurrentProject] = useState(() => readStoredJson(STORAGE_KEYS.currentProject));
  const [currentAnalysis, setCurrentAnalysis] = useState(() => readStoredJson(STORAGE_KEYS.currentAnalysis));
  const [settings, setSettings] = useState(null);
  const [activeJob, setActiveJob] = useState(null);

  useEffect(() => {
    writeStoredJson(STORAGE_KEYS.currentProject, currentProject);
  }, [currentProject]);

  useEffect(() => {
    writeStoredJson(STORAGE_KEYS.currentAnalysis, currentAnalysis);
  }, [currentAnalysis]);

  const loadSettings = useCallback(async () => {
    try {
      const data = await getSettings();
      setSettings(data);
      return data;
    } catch (e) {
      console.error('Failed to load settings:', e);
    }
  }, []);

  const saveSettings = useCallback(async (updates) => {
    try {
      await updateSettingsApi(updates);
      const data = await getSettings();
      setSettings(data);
      return data;
    } catch (e) {
      console.error('Failed to save settings:', e);
    }
  }, []);

  return (
    <AppContext.Provider value={{
      currentProject, setCurrentProject,
      currentAnalysis, setCurrentAnalysis,
      settings, setSettings, loadSettings, saveSettings,
      activeJob, setActiveJob,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be within AppProvider');
  return ctx;
};
